// src/components/Sidebar/Sidebar.js (updated)
import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  Typography,
  SwipeableDrawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  VolunteerActivism as VolunteerActivismIcon,
  Event as EventIcon,
  Description as DescriptionIcon,
  PhotoLibrary as PhotoLibraryIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { NAVBAR_HEIGHT } from "../Navbar";

export const DRAWER_WIDTH = 280;

const sections = [
  {
    title: "Main Menu",
    items: [
      { icon: <DashboardIcon />, label: "Dashboard", path: "/" },
      { icon: <PeopleIcon />, label: "Donors", path: "/Donors" },
      { icon: <PeopleIcon />, label: "Donations", path: "/donations" },
      { icon: <DescriptionIcon />, label: "CMS Pages", path: "/cms" },
      { icon: <DescriptionIcon />, label: "volunteers", path: "/volunteer" },
      {
        icon: <DescriptionIcon />,
        label: "Terms & Condition",
        path: "/termscondition",
      },
      {
        icon: <DescriptionIcon />,
        label: "Refund Policy ",
        path: "/refundpolicy",
      },
      {
        icon: <DescriptionIcon />,
        label: "Privacy Policy ",
        path: "/privacypolicy",
      },

      // { icon: <VolunteerActivismIcon />, label: 'Volunteers', path: '/volunteers' },
      // { icon: <EventIcon />, label: 'Causes & Events', path: '/causes' },
    ],
  },
  // {
  //   title: 'Content Management',
  //   items: [
  //     { icon: <DescriptionIcon />, label: 'CMS Pages', path: '/cms' },
  //     // { icon: <PhotoLibraryIcon />, label: 'Gallery', path: '/gallery' },
  //   ],
  // },
  // {
  //   title: 'Analytics',
  //   items: [{ icon: <BarChartIcon />, label: 'Reports', path: '/reports' }],
  // },
  // {
  //   title: 'Settings & Support',
  //   items: [
  //     { icon: <SettingsIcon />, label: 'Settings', path: '/settings' },
  //     { icon: <HelpIcon />, label: 'Support', path: '/support' },
  //     { icon: <LogoutIcon />, label: 'Logout', path: '/logout' },
  //   ],
  // },
];

/* ---------- Sidebar Content ---------- */
function SidebarContent({ onToggle, isMobile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [activePath, setActivePath] = useState(location.pathname);

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location]);

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) onToggle();
  };

  const renderList = (items) => (
    <List disablePadding>
      {items.map(({ icon, label, path }) => {
        const isActive = activePath === path;

        return (
          <ListItem
            key={label}
            button
            onClick={() => handleNavigation(path)}
            sx={{
              p: "12px 20px",
              gap: 1.5,
              cursor: "pointer",
              borderLeft: isActive
                ? "3px solid #00b894"
                : "3px solid transparent",
              color: isActive ? "#00b894" : "#6c757d",
              bgcolor: isActive ? "#f0fdfa" : "transparent",
              borderRadius: "0 25px 25px 0",
              mr: "10px",
              transition: "all .2s ease",
              "&:hover": {
                bgcolor: "#f8f9fa",
                color: "#00b894",
                transform: "translateX(5px)",
                "& .MuiSvgIcon-root": { color: "#00b894" },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 24,
                "& .MuiSvgIcon-root": {
                  fontSize: 20,
                  transition: "all .2s ease",
                  color: isActive ? "#00b894" : "#6c757d",
                },
              }}
            >
              {icon}
            </Box>
            <Typography
              fontSize={14}
              fontWeight={500}
              color={isActive ? "inherit" : "inherit"}
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {label}
            </Typography>
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <Box
      width={DRAWER_WIDTH}
      height="100%"
      bgcolor="#fff"
      display="flex"
      flexDirection="column"
    >
      {/* Header (mobile only) */}
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            height: NAVBAR_HEIGHT,
            px: 2,
            borderBottom: "1px solid #e0e6ed",
            bgcolor: "#ffffff",
            flexShrink: 0,
          }}
        >
          <IconButton onClick={onToggle}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Section lists - Scrollable area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          py: 1,
          scrollbarWidth: "none" /* Firefox */,
          "&::-webkit-scrollbar": {
            /* Chrome, Safari, Edge */ display: "none",
          },
          msOverflowStyle: "none",
        }}
      >
        {sections.map(({ title, items }) => (
          <Box key={title} py={1}>
            <Typography
              sx={{
                px: 2.5,
                pb: 0.5,
                fontSize: 11,
                fontWeight: 600,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: ".5px",
              }}
            >
              {title}
            </Typography>
            {renderList(items)}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ---------- Main Sidebar Component ---------- */
export default function Sidebar({ open, onToggle, navbarHeight = 0 }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <>
      {isMobile ? (
        <SwipeableDrawer
          anchor="left"
          open={open}
          onClose={onToggle}
          onOpen={onToggle}
          disableSwipeToOpen
          ModalProps={{ keepMounted: true }}
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              top: 0,
              height: "100vh",
              overflow: "hidden", // Prevent drawer overflow
            },
          }}
        >
          <SidebarContent onToggle={onToggle} isMobile={true} />
        </SwipeableDrawer>
      ) : (
        <Box
          sx={{
            width: DRAWER_WIDTH,
            position: "fixed",
            top: navbarHeight,
            left: 0,
            height: `calc(100vh - ${navbarHeight}px)`,
            borderRight: "1px solid #e0e6ed",
            bgcolor: "#ffffff",
            zIndex: theme.zIndex.drawer,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden", // Prevent sidebar overflow
          }}
        >
          <SidebarContent isMobile={false} onToggle={onToggle} />
        </Box>
      )}
    </>
  );
}
